import React, {useState} from 'react'
import {Modal, Button, Form, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {GET_CONTACT_TAGS, ASSIGN_CONTACT_TAGS, UNASSIGN_CONTACT_TAGS} from '../../../gql/queries/tags'
import {IResGetContactTags, IResAssignContactTags, IResUnassignContactTags} from '../../../types/tags'
import {toast} from 'react-toastify'
import './BulkTagModal.css'

interface BulkTagModalProps {
  show: boolean
  onHide: () => void
  selectedContactIds: string[]
  onSuccess?: () => void
}

export const BulkTagModal: React.FC<BulkTagModalProps> = ({
  show,
  onHide,
  selectedContactIds,
  onSuccess,
}) => {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [action, setAction] = useState<'assign' | 'unassign'>('assign')

  const {data, loading} = useQuery<IResGetContactTags>(GET_CONTACT_TAGS, {
    skip: !show,
  })

  const [assignTags, {loading: assigning}] = useMutation<IResAssignContactTags>(ASSIGN_CONTACT_TAGS)
  const [unassignTags, {loading: unassigning}] = useMutation<IResUnassignContactTags>(UNASSIGN_CONTACT_TAGS)

  const tags = data?.contactTags || []

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    )
  }

  const handleSubmit = async () => {
    if (selectedTagIds.length === 0) {
      toast.error('Please select at least one tag')
      return
    }

    try {
      if (action === 'assign') {
        const result = await assignTags({
          variables: {
            contactIds: selectedContactIds,
            tagIds: selectedTagIds,
          },
        })
        toast.success(`${result.data?.assignContactTags.assignedCount || 0} tags assigned successfully`)
      } else {
        const result = await unassignTags({
          variables: {
            contactIds: selectedContactIds,
            tagIds: selectedTagIds,
          },
        })
        toast.success(`${result.data?.unassignContactTags.unassignedCount || 0} tags removed successfully`)
      }
      
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error.message || 'Failed to update tags')
    }
  }

  const handleClose = () => {
    setSelectedTagIds([])
    setAction('assign')
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Tag Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='info' className='mb-4'>
          <i className='bi bi-info-circle me-2'></i>
          <strong>{selectedContactIds.length}</strong> contact{selectedContactIds.length !== 1 && 's'} selected
        </Alert>

        {/* Action Selection */}
        <div className='mb-4'>
          <h6>Action</h6>
          <div className='d-flex gap-3'>
            <Form.Check
              type='radio'
              id='action-assign'
              label='Add tags to selected contacts'
              checked={action === 'assign'}
              onChange={() => setAction('assign')}
            />
            <Form.Check
              type='radio'
              id='action-unassign'
              label='Remove tags from selected contacts'
              checked={action === 'unassign'}
              onChange={() => setAction('unassign')}
            />
          </div>
        </div>

        {/* Tag Selection */}
        <div className='tag-selection'>
          <h6 className='mb-3'>Select Tags</h6>
          {loading ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : tags.length === 0 ? (
            <Alert variant='warning'>
              No tags available. Create tags first in Tag Manager.
            </Alert>
          ) : (
            <div className='tags-grid'>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className={`tag-item ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
                  onClick={() => toggleTag(tag.id)}
                >
                  <Form.Check
                    type='checkbox'
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={() => toggleTag(tag.id)}
                    label={
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='tag-color'
                          style={{backgroundColor: tag.color}}
                        />
                        <span>{tag.name}</span>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant={action === 'assign' ? 'primary' : 'danger'}
          onClick={handleSubmit}
          disabled={assigning || unassigning || selectedTagIds.length === 0}
        >
          {assigning || unassigning ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Processing...
            </>
          ) : (
            <>
              {action === 'assign' ? 'Add Tags' : 'Remove Tags'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
